import { useModalStore } from '@/shared/lib';
import { ResultModal } from '@/features/withdraw-crypto/ui/ResultModal.tsx';
import {
  type openConfirmationModalProps,
  WithdrawConfirmationModalContainer,
  WithdrawCryptoModal,
  type WithdrawCryptoModalState,
} from '@/features/withdraw-crypto';
import { NUMERIC_CONFIG } from '@/shared/config';
import { useTranslation } from 'react-i18next';
// import { WithdrawHistoryModal } from '@/shared/components';
import { getCalculatedCommission } from '@/entities/operation';
import { useEditCryptoAddressModal } from '@/features/edit-crypto-address';
import type { SavedCryptoWalletAddress } from '@/entities/crypto-address';
import { useCreateSavedAddressModal } from '@/widgets/create-saved-address-modal';

export function useWithdrawCryptoModals() {
  const { showDialogComponent, closeDialog, getState, cleanup, getAllState } =
    useModalStore();
  const { t } = useTranslation();

  const openOperationMethodsModal = getState(
    'onWithdrawFormBack'
  ) as () => void;
  // TODO think about following FSD pattern
  const { openCreateSavedAddressModal } = useCreateSavedAddressModal();
  const { openEditCryptoAddressModal } = useEditCryptoAddressModal();

  const onCloseDialog = () => {
    closeDialog();
    cleanup();
  };

  const closeConfirmation = {
    title: t('withdrawMethods.crypto.exitConfirmTitle'),
    description: t('withdrawMethods.crypto.exitConfirmDescription'),
    confirmText: t('withdrawMethods.crypto.exitConfirmButton'),
    showCancelButton: false,
  };

  function getStoreData() {
    const storeData = getAllState<WithdrawCryptoModalState>();

    const commissionData = getCalculatedCommission({
      amount: storeData?.totalAmount || 0,
      assetCode: storeData?.withdrawCryptoFormData?.assetCode || '',
      includeFee: !!storeData?.withdrawCryptoFormData?.includeFee,
      fee: storeData?.fee || 0,
      precision:
        storeData?.selectedAsset?.precision || NUMERIC_CONFIG.precision.default,
    });

    return {
      commissionData,
      storeData,
    };
  }

  const openResultModal = ({ success }: { success: boolean }) => {
    const { commissionData, storeData } = getStoreData();

    showDialogComponent({
      component: (
        <ResultModal
          success={success}
          data={{
            commissionData,
            address: storeData?.withdrawCryptoFormData?.cryptoAddress || '',
          }}
          isNeedToSaveTemplate={storeData.isNeedToSaveTemplate}
          onClose={() => {
            if (success) {
              onCloseDialog();
              return;
            }

            openWithdrawFormModal();
          }}
          onSaveAddress={() =>
            openCreateSavedAddressModal({
              onBack: () => {
                onCloseDialog();
                openWithdrawFormModal();
              },
              onCreateSuccess: openOperationMethodsModal,
              showOnlyTab: 'crypto',
              initialCryptoFormValues: {
                assetCode: storeData?.withdrawCryptoFormData?.assetCode || '',
                networkName:
                  storeData?.withdrawCryptoFormData?.networkName || '',
                cryptoAddress:
                  storeData?.withdrawCryptoFormData?.cryptoAddress || '',
              },
            })
          }
        />
      ),
      onClose: onCloseDialog,
    });
  };

  // const openWithdrawHistory = () => {
  //   showDialogComponent({
  //     component: (
  //       <WithdrawHistoryModal
  //         onBack={openWithdrawFormModal}
  //         title={t('withdrawMethods.crypto.history.dialogTitle')}
  //       />
  //     ),
  //     onClose: onCloseDialog,
  //     closeConfirmation,
  //     closeConfirmationRequired: true,
  //     skipBackButtonConfirmation: true,
  //   });
  // };

  const handleEditSavedCryptoAddress = (
    savedCryptoAddress: SavedCryptoWalletAddress
  ) => {
    openEditCryptoAddressModal({
      savedCryptoAddress,
      onBack: openWithdrawFormModal,
    });
  };

  const openWithdrawFormModal = (assetCode?: string) => {
    const openOperationMethodsModal = getState(
      'onWithdrawFormBack'
    ) as () => void;

    showDialogComponent({
      component: (
        <WithdrawCryptoModal
          // Withdraw history is temporarily disabled
          // onHistoryClick={openWithdrawHistory}
          assetCode={assetCode}
          title={t('withdrawMethods.crypto.title')}
          onBack={openOperationMethodsModal}
          onEditSavedCryptoAddress={handleEditSavedCryptoAddress}
        />
      ),
      onClose: onCloseDialog,
      preventOverlayClose: true,
      closeConfirmation,
      skipBackButtonConfirmation: false,
    });
  };

  const openConfirmationModal = (params?: openConfirmationModalProps) => {
    const { commissionData, storeData } = getStoreData();

    showDialogComponent({
      component: (
        <WithdrawConfirmationModalContainer
          continueMFA={params?.continueMFA}
          onBack={openWithdrawFormModal}
          storeData={storeData}
          commissionData={commissionData}
        />
      ),
      onClose: onCloseDialog,
      preventOverlayClose: true,
      closeConfirmation,
      skipBackButtonConfirmation: true,
      closeConfirmationRequired: true,
    });
  };

  return {
    openResultModal,
    openWithdrawFormModal,
    openConfirmationModal,
  };
}
